<script setup>
import { computed } from 'vue';

const props = defineProps({
  widget: {
    type: Object,
    required: true,
  },
  widgetStyle: {
    type: Object,
    default: () => ({}),
  },
});

const title = computed(() => props.widget.title);
const steps = computed(() => props.widget.properties?.steps || []);

const isActive = status => ['current', 'completed'].includes(status);
</script>

<template>
  <div class="h-full flex flex-col font-inter group">
    <div
      class="flex items-center justify-between mb-10 pb-4 border-b border-slate-100 px-1"
    >
      <div class="flex items-center gap-3">
        <div
          class="flex h-7 w-7 items-center justify-center rounded-lg bg-slate-50 border border-slate-100 text-slate-400 group-hover:text-slate-900 group-hover:bg-white transition-all duration-200"
        >
          <span class="i-lucide-calendar-clock h-3.5 w-3.5" />
        </div>
        <h3 class="text-[13px] font-bold text-slate-900 tracking-tight">
          {{ title }}
        </h3>
      </div>
    </div>

    <div class="relative flex items-center justify-between w-full px-10 pb-12">
      <!-- Line background -->
      <div
        class="absolute top-1/2 left-0 w-full h-px bg-slate-100 -z-10 transform -translate-y-1/2"
      />

      <!-- Progress line -->
      <div
        class="absolute top-1/2 left-0 h-px bg-slate-950 -z-10 transform -translate-y-1/2 transition-all duration-1000"
        :style="{ width: '100%' }"
      />

      <!-- Steps -->
      <div
        v-for="(step, index) in steps"
        :key="index"
        class="relative flex flex-col items-center group/step flex-1"
      >
        <!-- Step Circle -->
        <div
          class="w-10 h-10 rounded-full flex items-center justify-center transition-all duration-300 z-10 border bg-white shadow-sm"
          :class="
            step.status === 'completed'
              ? 'border-slate-950'
              : step.status === 'current'
                ? 'border-slate-950 border-dashed'
                : 'border-slate-100'
          "
        >
          <div
            class="w-full h-full flex items-center justify-center rounded-full transition-colors duration-300"
            :class="isActive(step.status) ? 'bg-slate-50' : 'bg-transparent'"
          >
            <span
              v-if="step.status === 'completed'"
              class="i-lucide-check text-slate-950 size-5 font-bold"
            />
            <div
              v-else-if="step.status === 'current'"
              class="w-2 h-2 rounded-full bg-slate-950 animate-pulse"
            />
            <div v-else class="w-1.5 h-1.5 rounded-full bg-slate-200" />
          </div>
        </div>

        <!-- Label -->
        <div class="absolute top-14 w-32 text-center px-2">
          <span
            class="text-[11px] font-bold uppercase tracking-widest transition-all duration-300"
            :class="isActive(step.status) ? 'text-slate-900' : 'text-slate-400'"
          >
            {{ step.label }}
          </span>
        </div>
      </div>
    </div>
  </div>
</template>
